<template>
  <div class="relative space-y-6 lg:space-y-8 px-3 sm:px-4 lg:px-8 min-h-[calc(100vh-8rem)]">
    <div class="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
      <div>
        <h1 class="text-2xl sm:text-3xl lg:text-4xl font-bold text-primary dark:text-secondary">
          Employee Documents
        </h1>
        <p class="text-sm text-slate-500 dark:text-slate-200 mt-2">
          View important documents uploaded by employees.
        </p>
      </div>
    </div>

    <TableControls
      v-model:search="searchQuery"
      v-model:status="statusFilter"
      v-model:perPage="perPage"
      placeholder="Search by title or employee..."
      :options="[{ value: '', label: 'All' }]"
      filterLabel="Filter"
      class="md:sticky md:top-[80px]"
    />

    <div v-if="error" class="bg-red-50 text-red-600 p-4 rounded-xl border border-red-200">
      {{ error }}
    </div>

    <div v-else class="bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-slate-200/50 dark:border-slate-700/50 overflow-hidden">
      <!-- Desktop Table View -->
      <div class="hidden lg:block overflow-x-auto">
        <table class="min-w-full divide-y divide-slate-200/50 dark:divide-slate-700/50">
          <thead class="bg-slate-50 dark:bg-slate-800/50">
            <tr>
              <th class="px-6 py-4 text-left text-xs font-bold text-slate-700 dark:text-slate-300 uppercase">Employee</th>
              <th class="px-6 py-4 text-center text-xs font-bold text-slate-700 dark:text-slate-300 uppercase">Document Title</th>
              <th class="px-6 py-4 text-center text-xs font-bold text-slate-700 dark:text-slate-300 uppercase">Uploaded At</th>
              <th class="px-6 py-4 text-center text-xs font-bold text-slate-700 dark:text-slate-300 uppercase">Action</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-slate-200/50 dark:divide-slate-700/50">
            <TableLoader v-if="loading" :colspan="4" title="Fetching Documents" />
            <tr v-else-if="paginatedDocuments.length === 0">
              <td colspan="4" class="px-6 py-12 text-center text-slate-500">No documents found.</td>
            </tr>
            <tr v-for="doc in paginatedDocuments" :key="doc.id" class="hover:bg-slate-50 dark:hover:bg-slate-800/50">
              <td class="px-6 py-4">
                <div class="flex items-center gap-3 justify-start">
                  <template v-if="shouldShowProfileImage(doc)">
                    <img
                      :src="profileImageUrl(doc)"
                      :alt="`${employeeName(doc)} photo`"
                      class="h-10 w-10 rounded-full object-cover shadow-md flex-shrink-0"
                      @error="onProfileImageError(doc)"
                    />
                  </template>
                  <div
                    v-else
                    class="h-10 w-10 rounded-full flex items-center justify-center text-white font-bold shadow-md bg-gradient-to-br flex-shrink-0"
                    :class="avatarBgClass(doc)"
                  >
                    {{ employeeInitials(doc) }}
                  </div>
                  <div class="min-w-0 text-left">
                    <div class="text-sm font-semibold text-slate-700 dark:text-slate-300">
                      {{ employeeName(doc) }}
                    </div>
                    <div class="text-sm text-slate-500 dark:text-slate-400">
                      {{ doc.user?.email || '' }}
                    </div>
                  </div>
                </div>
              </td>
              <td class="px-6 py-4 text-center">
                {{ doc.title }}
              </td>
              <td class="px-6 py-4 text-center">
                {{
                  `${new Date(doc.created_at).toLocaleDateString('en-US', {
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric'
                  })} [${new Date(doc.created_at).toLocaleTimeString('en-US')}]`
                }}
              </td>
              <td class="px-6 py-4">
                <div class="flex gap-2 justify-center">
                  <button @click="viewDocument(doc)" class="p-2 text-blue-600 hover:bg-primary/10 rounded-lg transition-colors" title="View">
                    <Eye class="w-5 h-5" />
                  </button>
                  <button @click="downloadDocument(doc)" class="p-2 text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors" title="Download">
                    <DownloadIcon class="w-5 h-5" />
                  </button>
                  <button @click="printDocument(doc)" class="p-2 text-purple-600 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded-lg transition-colors" title="Print">
                    <Printer class="w-5 h-5" />
                  </button>
                  <button @click="handleDelete(doc)" class="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors" title="Delete">
                    <Trash class="w-5 h-5" />
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Mobile Card View -->
      <div class="lg:hidden divide-y divide-slate-200/50 dark:divide-slate-700/50">
        <div v-if="loading" class="p-8 flex justify-center">
          <Loader2 class="w-8 h-8 animate-spin text-primary" />
        </div>
        <div v-else-if="paginatedDocuments.length === 0" class="px-6 py-12 text-center text-slate-500">No documents found.</div>
        <div v-else v-for="doc in paginatedDocuments" :key="doc.id" class="p-4 hover:bg-slate-50 dark:hover:bg-slate-800/50">
          <div class="flex items-center gap-3 mb-2">
            <template v-if="shouldShowProfileImage(doc)">
              <img :src="profileImageUrl(doc)" :alt="`${employeeName(doc)} photo`" class="h-10 w-10 rounded-full object-cover shadow-md flex-shrink-0" @error="onProfileImageError(doc)" />
            </template>
            <div v-else class="h-10 w-10 rounded-full flex items-center justify-center text-white font-bold shadow-md bg-gradient-to-br flex-shrink-0" :class="avatarBgClass(doc)">
              {{ employeeInitials(doc) }}
            </div>
            <div class="min-w-0 flex-1">
              <div class="text-sm font-semibold text-slate-700 dark:text-slate-300">{{ employeeName(doc) }}</div>
              <div class="text-xs text-slate-500 dark:text-slate-400 truncate">{{ doc.user?.email || '' }}</div>
            </div>
          </div>
          <div class="text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">{{ doc.title }}</div>
          <div class="text-xs text-slate-500 dark:text-slate-400 mb-2">{{ new Date(doc.created_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) }} [{{ new Date(doc.created_at).toLocaleTimeString('en-US') }}]</div>
          <div class="flex gap-2">
            <button @click="viewDocument(doc)" class="p-2 text-blue-600 hover:bg-primary/10 rounded-lg transition-colors" title="View">
              <Eye class="w-5 h-5" />
            </button>
            <button @click="downloadDocument(doc)" class="p-2 text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors" title="Download">
              <DownloadIcon class="w-5 h-5" />
            </button>
            <button @click="printDocument(doc)" class="p-2 text-purple-600 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded-lg transition-colors" title="Print">
              <Printer class="w-5 h-5" />
            </button>
            <button @click="handleDelete(doc)" class="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors" title="Delete">
              <Trash class="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="!loading && !error && filteredTotal > 0"
      class="card border border-gray-200/50 dark:border-gray-700/50 shadow-sm sticky bottom-0 z-30"
    >
      <div class="p-4">
        <PaginationBar
          :current-page="currentPage"
          :total-pages="filteredTotalPages"
          :total="filteredTotal"
          :per-page="perPage"
          @update:page="goToPage"
        />
      </div>
    </div>
    <!-- Delete Confirmation Modal -->
    <!-- Delete Confirmation Modal -->
    <!-- Delete Confirmation Modal -->
    <Modal v-model="showDeleteModal" title="Delete Document">
      <p class="text-sm text-secondary-text/80 dark:text-secondary-dark-text/80">
        Are you sure you want to permanently delete
        <span class="font-semibold text-slate-700 dark:text-slate-300">"{{ pendingDeleteTitle }}"</span>?
        This action cannot be undone.
      </p>
      <template #footer>
        <button type="button"
          class="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-sm font-medium text-secondary-text dark:text-secondary-dark-text hover:bg-gray-100/80 dark:hover:bg-gray-700/60 transition-colors duration-200"
          @click="showDeleteModal = false">Cancel</button>
        <button type="button"
          class="ml-2 px-4 py-2 rounded-lg text-sm font-semibold text-white bg-danger hover:bg-danger/90 shadow-sm hover:shadow-md transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
          :disabled="deleteLoading" @click="confirmDelete">
          {{ deleteLoading ? 'Deleting...' : 'Yes, delete permanently' }}
        </button>
      </template>
    </Modal>

    <!-- View Document Modal -->
    <DocumentDetailsModal
      :is-open="showViewModal"
      :document="selectedDocument"
      @close="showViewModal = false; selectedDocument = null"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { employeeDocumentService, type EmployeeDocument } from '@/services/employeeDocumentService'
import { Eye, Trash, Download as DownloadIcon, Printer, Loader2, X } from 'lucide-vue-next'
import { useToast } from 'vue-toastification'
import TableControls from '@/components/layout/TableControls.vue'
import PaginationBar from '@/components/layout/PaginationBar.vue'
import TableLoader from '@/components/ui/TableLoader.vue'
import Modal from '@/components/layout/Modal.vue'
import DocumentDetailsModal from '@/components/documents/DocumentDetailsModal.vue'
import api from '@/services/api'

const toast = useToast()
const toastTimeout = 5000

const documents = ref<EmployeeDocument[]>([])
const loading = ref(false)
const error = ref('')

const searchQuery = ref('')
const statusFilter = ref('')
const perPage = ref(10)
const currentPage = ref(1)

const avatarLoadFailed = ref<Set<number>>(new Set())

const profileImageUrl = (doc: EmployeeDocument) => {
  const raw = String(doc.user?.profile_picture || '').trim()
  if (!raw || raw.toLowerCase() === 'null') return ''
  if (/^https?:\/\//i.test(raw) || raw.startsWith('data:image/') || raw.startsWith('blob:')) return raw
  const rawBase = String(api.defaults.baseURL || '').replace(/\/$/, '')
  const baseNoApi = rawBase.replace(/\/api$/i, '')
  if (raw.startsWith('/storage/')) return `${baseNoApi}${raw}`
  if (raw.startsWith('storage/')) return `${baseNoApi}/${raw}`
  return `${baseNoApi}/storage/${raw.replace(/^\//, '')}`
}

const shouldShowProfileImage = (doc: EmployeeDocument) => {
  const url = profileImageUrl(doc)
  return Boolean(url) && !avatarLoadFailed.value.has(doc.id)
}

const onProfileImageError = (doc: EmployeeDocument) => {
  avatarLoadFailed.value.add(doc.id)
}

const employeeName = (doc: EmployeeDocument) => {
  const f = doc.user?.details?.first_name || ''
  const l = doc.user?.details?.last_name || ''
  if (f && l) return `${f} ${l}`
  return doc.user?.email || 'Unknown'
}

const employeeInitials = (doc: EmployeeDocument) => {
  const f = doc.user?.details?.first_name || ''
  const l = doc.user?.details?.last_name || ''
  if (f && l) return (f[0] + l[0]).toUpperCase()
  const email = doc.user?.email || ''
  if (email) return email.substring(0, 2).toUpperCase()
  return 'EM'
}

const avatarBgClass = (doc: EmployeeDocument) => {
  const gradients = [
    'from-primary to-primary-hover dark:from-primary-dark dark:to-primary-dark-hover',
    'from-success to-success-hover dark:from-success-dark dark:to-success-dark-hover',
    'from-info to-info-hover dark:from-info-dark dark:to-info-dark-hover',
    'from-warning to-warning-hover dark:from-warning-dark dark:to-warning-dark-hover',
    'from-danger to-danger-hover dark:from-danger-dark dark:to-danger-dark-hover',
  ]
  return gradients[doc.user_id % gradients.length]
}

const searchFiltered = computed(() => {
  if (!searchQuery.value.trim()) return documents.value
  const q = searchQuery.value.toLowerCase()
  return documents.value.filter(d => {
    const name = ((d.user?.details?.first_name || '') + ' ' + (d.user?.details?.last_name || '')).toLowerCase()
    const title = (d.title || '').toLowerCase()
    return name.includes(q) || title.includes(q)
  })
})

const filteredTotal = computed(() => searchFiltered.value.length)
const filteredTotalPages = computed(() => Math.max(1, Math.ceil(searchFiltered.value.length / perPage.value)))

const paginatedDocuments = computed(() => {
  const start = (currentPage.value - 1) * perPage.value
  return searchFiltered.value.slice(start, start + perPage.value)
})

watch([searchQuery, statusFilter, perPage], () => {
  currentPage.value = 1
})

const goToPage = (page: number) => {
  currentPage.value = page
}

const fetchDocuments = async () => {
  loading.value = true
  error.value = ''
  try {
    documents.value = await employeeDocumentService.fetchAll()
  } catch (err: any) {
    error.value = 'Failed to load employee documents.'
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  fetchDocuments()
})

const showViewModal = ref(false)
const selectedDocument = ref<EmployeeDocument | null>(null)
const showDeleteModal = ref(false)
const deleteLoading = ref(false)
const pendingDeleteId = ref<number | null>(null)
const pendingDeleteTitle = ref('')

const viewDocument = (doc: EmployeeDocument) => {
  selectedDocument.value = doc
  showViewModal.value = true
}

const downloadDocument = async (doc: EmployeeDocument) => {
  const loadingToast = toast.info('Preparing document for download...', { timeout: false })
  try {
    const token = localStorage.getItem('token')
    const url = employeeDocumentService.getDownloadUrl(doc.id)
    const response = await fetch(url, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
    if (!response.ok) throw new Error('Invalid File')
    const blob = await response.blob()
    const blobUrl = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = blobUrl
    a.download = doc.title || 'download'
    document.body.appendChild(a)
    a.click()
    window.URL.revokeObjectURL(blobUrl)
    document.body.removeChild(a)
    toast.dismiss(loadingToast)
    toast.success('Document downloaded successfully', { timeout: toastTimeout })
  } catch {
    toast.dismiss(loadingToast)
    toast.error('Failed to download document', { timeout: toastTimeout })
  }
}

const printDocument = async (doc: EmployeeDocument) => {
  const loadingToast = toast.info('Preparing document for printing...', { timeout: false })
  try {
    await employeeDocumentService.printDocument(doc.id)
    toast.dismiss(loadingToast)
  } catch (err: any) {
    toast.dismiss(loadingToast)
    if (!err.message?.includes('opened in a new tab')) {
      toast.error(err.message || 'Failed to print document', { timeout: toastTimeout })
    }
  }
}

const handleDelete = (doc: EmployeeDocument) => {
  pendingDeleteId.value = doc.id
  pendingDeleteTitle.value = doc.title || 'this document'
  showDeleteModal.value = true
}

const confirmDelete = async () => {
  if (pendingDeleteId.value === null) return
  deleteLoading.value = true
  try {
    await employeeDocumentService.delete(pendingDeleteId.value)
    toast.success('Document deleted successfully', { timeout: toastTimeout })
    showDeleteModal.value = false
    pendingDeleteId.value = null
    fetchDocuments()
  } catch {
    toast.error('Failed to delete document', { timeout: toastTimeout })
  } finally {
    deleteLoading.value = false
  }
}
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
